import { isOfferedForMidTerm } from './settings.js'
import { isStay } from '../booking/reservationStatus.js'

/**
 * WHAT STATE A HOME IS IN ON THIS PLATFORM, AND WHAT EACH STATE DOES. (#199, Product Owner 2026-09-29.)
 *
 * Modelled on Guesty's own ladder (Unlist → Deactivate → Delete), with one change the Product
 * Owner asked for: "Delete" is what the button says, and what it does is archive — nothing is
 * erased, the home moves to Archive and can be restored a year later.
 *
 *   LISTED       on the platform: Listing, the renter's page, quotes, Calendar, automations
 *   UNLISTED     not offered to renters (Listing, renter's page, quotes) — everything else as usual
 *   DEACTIVATED  out of the working product; kept, and findable under the Hidden tab; its
 *                automations stop; a stay still to come keeps it on the Calendar
 *   DELETED      out of everything, Calendar included; kept in Archive, restorable
 *
 * A home switched off in Guesty is OFF_IN_GUESTY: treated as deactivated, and only Guesty turns it on.
 */
export const HOME_STATUS = Object.freeze({
  LISTED: 'LISTED', UNLISTED: 'UNLISTED', DEACTIVATED: 'DEACTIVATED', DELETED: 'DELETED', OFF_IN_GUESTY: 'OFF_IN_GUESTY',
})

export const STATUS_LABEL = {
  LISTED: 'Listed', UNLISTED: 'Unlisted', DEACTIVATED: 'Deactivated', DELETED: 'Deleted', OFF_IN_GUESTY: 'Off in Guesty',
}

/**
 * The state of one home. `statuses` is what was decided here ({ [unitId]: { status, at, by } });
 * a home nobody has decided on is Listed — except that the old "mid-term" switch, where somebody
 * turned it off, still counts as Deactivated, which is what that switch did.
 */
export function statusOf(unit, { statuses = {}, settings = null } = {}) {
  const decided = statuses[unit?.id]?.status
  /* Deleted wins even over Guesty: a home switched off there can still be put in Archive here. */
  if (decided === HOME_STATUS.DELETED) return decided
  if (unit?.active === false) return HOME_STATUS.OFF_IN_GUESTY
  if (decided && HOME_STATUS[decided]) return decided
  return isOfferedForMidTerm(settings) ? HOME_STATUS.LISTED : HOME_STATUS.DEACTIVATED
}

/** Offered to renters: Listing, the public page, Create quote. */
export const isOfferable = (status) => status === HOME_STATUS.LISTED
/** In the working lists: Property setup, pickers, counts, automations. */
export const isWorking = (status) => status === HOME_STATUS.LISTED || status === HOME_STATUS.UNLISTED
/** On the Calendar: working homes, and a hidden one while it still has a stay to come. */
export const isOnCalendar = (status, { stayAhead = false } = {}) =>
  isWorking(status) || (stayAhead && status !== HOME_STATUS.DELETED)

/**
 * What pressing each button does, in the words the confirmation shows. `impact` is what this home
 * has right now, so the sentences carry numbers rather than warnings nobody reads.
 */
export function consequences(action, impact = {}) {
  const n = (k) => impact[k] ?? 0
  const stays = n('futureStays'); const tasks = n('openTasks'); const rules = n('activeRules'); const convos = n('conversations')
  const kept = 'Nothing is erased: past stays, conversations, leads, quotes and this page\'s figures are all kept.'
  if (action === 'UNLIST') {
    return { title: 'Unlist this home?', does: [
      'Renters can no longer find it: it leaves Listing, its public page stops opening within a minute, and quotes stop suggesting it.',
      'Everything else carries on — Calendar, tasks and automations as usual.',
    ], kept, confirm: 'Unlist' }
  }
  if (action === 'DEACTIVATE') {
    return { title: 'Deactivate this home?', does: [
      'It leaves Listing, its public page, quotes, and the lists and counts here (it moves to the Hidden tab).',
      rules ? `Its automations stop: none of the ${rules} switched-on ${rules === 1 ? 'rule' : 'rules'} about stays, holds, deals or tasks will create anything for it.` : 'Automations stop creating tasks or messages for it.',
      tasks ? `${tasks} open ${tasks === 1 ? 'task stays' : 'tasks stay'} as they are — close or reassign them yourself.` : null,
      stays ? `It still has ${stays} ${stays === 1 ? 'stay' : 'stays'} to come: it stays on the Calendar until ${stays === 1 ? 'that guest has' : 'those guests have'} left.` : 'It leaves the Calendar.',
      convos ? `${convos} ${convos === 1 ? 'conversation mentions' : 'conversations mention'} this home; they stay in the Inbox, marked that the home is off.` : null,
    ].filter(Boolean), kept, confirm: 'Deactivate', needsTick: true }
  }
  if (action === 'DELETE') {
    return { title: 'Delete this home?', does: [
      'It disappears from everywhere on this platform — Listing, quotes, Calendar, every list and count.',
      'Its automations stop.',
      'It moves to Archive. You can restore it from there at any time, as it was.',
    ], kept, confirm: 'Delete', needsName: true,
    blocked: stays ? `It still has ${stays} ${stays === 1 ? 'stay' : 'stays'} to come. Unlist or deactivate it instead, and delete it once ${stays === 1 ? 'that guest has' : 'those guests have'} left.` : null }
  }
  return null
}

/** Which buttons a home in each state offers, in order. */
const ACTIONS_FOR = {
  LISTED: [['UNLIST', 'Unlist'], ['DEACTIVATE', 'Deactivate'], ['DELETE', 'Delete']],
  UNLISTED: [['LIST', 'List again'], ['DEACTIVATE', 'Deactivate'], ['DELETE', 'Delete']],
  DEACTIVATED: [['REACTIVATE', 'Reactivate'], ['DELETE', 'Delete']],
  DELETED: [['RESTORE', 'Restore']],
  OFF_IN_GUESTY: [['DELETE', 'Delete']],
}

/** The state each button leads to. Restore and Reactivate bring a home back Listed. */
export const RESULT_OF = { UNLIST: 'UNLISTED', LIST: 'LISTED', DEACTIVATE: 'DEACTIVATED', REACTIVATE: 'LISTED', DELETE: 'DELETED', RESTORE: 'LISTED' }

/** Every home's state, by id — computed once, so every screen asks the same question the same way. */
export function statusesById(units = [], { statuses = {}, settingsByUnit = {} } = {}) {
  return Object.fromEntries(units.map((u) => [u.id, statusOf(u, { statuses, settings: settingsByUnit[u.id] ?? null })]))
}

/**
 * THE HOMES WHOSE AUTOMATIONS ARE STOPPED: the ones a person deactivated or deleted here, and only
 * those. Not a home that is merely off in Guesty, and not one switched off with the older mid-term
 * switch — automations ran for both until now, and a rent reminder that quietly stopped because of
 * a switch nobody here pressed would be the worst kind of change. The button says it stops them;
 * so the button is what stops them.
 */
export function pausedUnitIds(statuses = {}) {
  return Object.entries(statuses)
    .filter(([, r]) => r?.status === HOME_STATUS.DEACTIVATED || r?.status === HOME_STATUS.DELETED)
    .map(([id]) => id)
}

/*
  WHAT COUNTS AS A GUEST STILL TO COME. Every stay, SHORT ONES INCLUDED — a four-night booking next
  week is a guest arriving at a home somebody is about to delete. So this is read from Guesty's own
  reservations (not from the Reservations table, which keeps only stays of 30 nights and more) plus
  this product's own mid-term stays, once each. The statuses that mean nobody is staying are the
  calendar's own (calendar/reservations.js).
*/
export function staysOf({ vendor = null, built = [] } = {}) {
  const byId = new Map()
  for (const r of vendor ?? []) if (isStay(r.status)) byId.set(r.id, r)
  for (const r of built) if (!byId.has(r.id)) byId.set(r.id, r)
  return [...byId.values()]
}
const ahead = (r, today) => Boolean(r.checkOut) && String(r.checkOut).slice(0, 10) > today
const unitOf = (r) => r.unit?.id ?? r.unitId

/** The homes with a stay that has not ended — from `staysOf`. */
export const homesWithStayAhead = (stays = [], today) => new Set(stays.filter((r) => ahead(r, today)).map(unitOf))

/** Every home a lead has been about: a quoted option, a hold, a deal. A lead names no home itself. */
export function homesOfLead(lead, { holds = [], deals = {} } = {}) {
  const ids = new Set()
  for (const q of lead?.quotes ?? []) for (const o of q.options ?? []) if (o.unitId) ids.add(o.unitId)
  for (const h of holds) if (h.leadId === lead?.id && h.unitId) ids.add(h.unitId)
  const deal = deals[lead?.id]
  if (deal?.unitId) ids.add(deal.unitId)
  return [...ids]
}

/*
  The events a rule can hear that are ABOUT A HOME — they carry its id (automation/events.js). The
  lead's own moments (a message waiting, an enquiry, a stage) carry none, and pausing a home does not
  stop them: the renter is still owed an answer.
*/
const LEAD_ONLY = new Set(['LEAD_DETECTED', 'LEAD_CONFIRMED', 'RENTER_WAITING', 'MISSED_CALL', 'ENQUIRY_RECEIVED', 'QUOTE_SENT', 'LEAD_NOT_WON'])
const TASK_DONE = new Set(['DONE', 'CANCELLED'])

/** What a confirmation should say this home has right now — the numbers `consequences` prints. */
export function homeImpact(unitId, { stays = [], tasks = [], rules = [], leads = [], holds = [], deals = {}, today }) {
  return {
    futureStays: stays.filter((r) => unitOf(r) === unitId && ahead(r, today)).length,
    openTasks: tasks.filter((t) => t.unitId === unitId && !TASK_DONE.has(t.status)).length,
    activeRules: rules.filter((r) => r.active && !LEAD_ONLY.has(r.trigger?.event)).length,
    conversations: leads.filter((l) => homesOfLead(l, { holds, deals }).includes(unitId)).length,
  }
}

/** One stored row per home → { [propertyId]: { status, at, by } }, the shape every screen reads. */
export const homeStatusesFromRows = (rows = []) => Object.fromEntries(rows.map((r) =>
  [r.property_id, { status: r.status, at: r.changed_at ?? null, by: r.who ?? null }]))

/**
 * WHICH OF THOSE BUTTONS STAY IN VIEW (design audit #10, rule R3). The first thing that is not Delete
 * — Unlist, List again, Reactivate, Restore — is the one people press, so it stays a button; the rest
 * (Deactivate, Delete) go behind "⋯", Delete last. Each still asks first, exactly as before.
 */
export function splitActions(status) {
  const all = ACTIONS_FOR[status] ?? []
  const first = all.find(([action]) => action !== 'DELETE') ?? null
  return { shown: first ? [first] : [], more: all.filter((a) => a !== first) }
}
